import { useAuth } from '@/shared/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientAPI } from '@/shared/lib/api';
import {
  Plane,
  Award,
  RefreshCw,
  Loader2,
  AlertCircle,
  User,
  Clock,
  Calendar,
  CalendarClock,
  TrendingUp,
  DollarSign,
} from 'lucide-react';
import { getProgramLogo } from '@/utils/program-assets';
import { formatDate, formatCurrency } from '@/shared/lib/utils';
import { useState } from 'react';
import * as React from 'react';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@travellegacy/nexops-design-system';
import { RenewalExpiredAlert } from '@/features/client/components/RenewalExpiredAlert';
import { FavoritePartnersSection } from '@/features/client/components/FavoritePartnersSection';

interface ClientProgram {
  id: string;
  programName: string;
  points: number;
  tier: string | null;
  ownership: string | null;
  pointsExpiring: number | null;
  expirationDate: string | null;
  manualUpdateDate: string | null;
  lastUpdated: string;
}

interface Emission {
  id: string;
  trecho: string;
  tipo: string | null;
  locCompanhia: string | null;
  dataHoraIda: string | null;
  dataHoraVolta: string | null;
  companhia: string | null;
  classe: string | null;
  vEconomizado: number | null;
  iataOrigem: string | null;
  iataDestino: string | null;
  temConexao: boolean | null;
  withMiles: boolean;
  sobrenome: string | null;
  taxa: number | null; // Taxa (emissões com milhas)
  vVenda: number | null; // V. Venda (emissões sem milhas)
  vCompanhia: number | null; // V. Companhia (ambos)
}

// Parse date string without timezone conversion
const parseDate = (dateString: string): Date => {
  const date = new Date(dateString);
  // If the date string is in ISO format without time (YYYY-MM-DD),
  // create a new date using UTC to avoid timezone shifts
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return date;
};

// Verificar se uma data é futura ou passada e retornar classes CSS
const getDateColorClasses = (dateString: string | null): string => {
  if (!dateString) return 'text-muted-foreground';

  const date = new Date(dateString);
  const now = new Date();

  // Comparar apenas as datas, ignorando horas
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (dateOnly >= nowOnly) {
    // Data futura ou hoje - verde
    return 'text-green-600 dark:text-green-400 font-semibold';
  } else {
    // Data passada - vermelho
    return 'text-red-600 dark:text-red-400';
  }
};

// Calcular tempo até a viagem
const getTimeUntilTrip = (dateString: string | null): string => {
  if (!dateString) return '';

  const tripDate = new Date(dateString);
  const now = new Date();
  const diffMs = tripDate.getTime() - now.getTime();

  if (diffMs < 0) return 'Viagem já passou';

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return `em ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `em ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  } else if (diffMins > 0) {
    return `em ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
  } else {
    return 'Agora!';
  }
};

// Format date to relative time or absolute date/time
const formatUpdateDate = (dateString: string | null): { text: string; isOld: boolean } => {
  if (!dateString) return { text: 'Nunca atualizado', isOld: false };

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Se menos de 1 hora, mostra minutos
  if (diffMins < 60) {
    return {
      text: diffMins <= 1 ? 'Agora mesmo' : `${diffMins} min atrás`,
      isOld: false,
    };
  }

  // Se menos de 24 horas, mostra horas
  if (diffHours < 24) {
    return { text: `${diffHours}h atrás`, isOld: false };
  }

  // Se menos de 7 dias, mostra dias
  if (diffDays < 7) {
    return { text: `${diffDays}d atrás`, isOld: false };
  }

  // Se menos de 30 dias, mostra data completa
  if (diffDays < 30) {
    return {
      text: date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        hour: '2-digit',
        minute: '2-digit',
      }),
      isOld: false,
    };
  }

  // Se 30 dias ou mais, mostra data absoluta sem ano e marca como antigo (laranja)
  return {
    text: date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    }),
    isOld: true,
  };
};

// Cores para os tipos de ownership (valores exatos do Notion)
const getOwnershipColor = (ownership: string | null): string => {
  if (!ownership) return 'bg-muted text-muted-foreground';

  // Match exato com os valores do Notion
  if (ownership === 'Titular')
    return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
  if (ownership === 'Cônjuge')
    return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
  if (ownership === 'Filho(a)')
    return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
  if (ownership === 'Pai/Mãe')
    return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300';
  if (ownership === 'Sócio(a)')
    return 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300';
  if (ownership === 'Empresarial')
    return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300';
  if (ownership === 'Outro')
    return 'bg-slate-100 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300';

  return 'bg-muted text-muted-foreground';
};

// Ordem de prioridade para exibição dos ownerships (valores exatos do Notion)
const getOwnershipPriority = (ownership: string | null): number => {
  if (!ownership) return 99; // Sem ownership por último

  // Match exato com os valores do Notion
  if (ownership === 'Titular') return 1;
  if (ownership === 'Cônjuge') return 2;
  if (ownership === 'Filho(a)') return 3;
  if (ownership === 'Pai/Mãe') return 4;
  if (ownership === 'Sócio(a)') return 5;
  if (ownership === 'Empresarial') return 6;
  if (ownership === 'Outro') return 7;

  return 98; // Desconhecido antes de null
};

// Verificar se a data de renovação já passou
const isRenewalExpired = (renewalDate: string | null): boolean => {
  if (!renewalDate) return false;

  const renewal = new Date(renewalDate);
  const now = new Date();

  // Comparar apenas as datas, ignorando horas
  const renewalOnly = new Date(renewal.getFullYear(), renewal.getMonth(), renewal.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return renewalOnly < nowOnly;
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [syncMessage, setSyncMessage] = useState<{
    type: 'success' | 'error' | 'loading';
    text: string;
  } | null>(null);
  const [activeEmissionTab, setActiveEmissionTab] = useState<string>('');

  // Buscar programas do cliente
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['client-dashboard'],
    queryFn: async () => {
      const response = await clientAPI.getDashboard();
      return response.data.data;
    },
    refetchInterval: 5 * 60 * 1000, // Refetch a cada 5 minutos
  });

  // Buscar emissões do cliente
  const { data: emissionsData, isLoading: isLoadingEmissions } = useQuery({
    queryKey: ['client-emissions'],
    queryFn: async () => {
      const response = await clientAPI.getEmissions();
      return response.data.data as { withMiles: Emission[]; withoutMiles: Emission[] };
    },
    refetchInterval: 5 * 60 * 1000, // Refetch a cada 5 minutos
  });

  // Buscar próxima viagem do cliente
  const { data: nextTripData, isLoading: isLoadingNextTrip } = useQuery({
    queryKey: ['client-next-trip'],
    queryFn: async () => {
      const response = await clientAPI.getNextTrip();
      return response.data.data as Emission | null;
    },
    refetchInterval: 5 * 60 * 1000, // Refetch a cada 5 minutos
  });

  // Filtrar emissões futuras (tanto com milhas quanto sem milhas)
  const upcomingFlights = React.useMemo(() => {
    if (!emissionsData) return [];

    const now = new Date();
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const allEmissions = [...emissionsData.withMiles, ...emissionsData.withoutMiles];

    return allEmissions
      .filter((emission) => {
        if (!emission.dataHoraIda) return false;

        const idaDate = new Date(emission.dataHoraIda);
        const idaDateOnly = new Date(idaDate.getFullYear(), idaDate.getMonth(), idaDate.getDate());

        // Incluir voo se a ida é futura OU se a volta é futura
        const idaIsFuture = idaDateOnly >= nowOnly;

        if (idaIsFuture) return true;

        // Se ida já passou, verificar se a volta ainda vai acontecer
        if (emission.dataHoraVolta) {
          const voltaDate = new Date(emission.dataHoraVolta);
          const voltaDateOnly = new Date(voltaDate.getFullYear(), voltaDate.getMonth(), voltaDate.getDate());
          return voltaDateOnly >= nowOnly;
        }

        return false;
      })
      .sort((a, b) => {
        // Ordenar por data de ida (mais próximo primeiro)
        const dateA = a.dataHoraIda ? new Date(a.dataHoraIda).getTime() : 0;
        const dateB = b.dataHoraIda ? new Date(b.dataHoraIda).getTime() : 0;
        return dateA - dateB;
      });
  }, [emissionsData]);

  // Definir aba ativa das emissões baseado no que tem dados
  React.useEffect(() => {
    if (emissionsData) {
      // Priorizar "Próximos Voos" se houver voos futuros
      if (upcomingFlights.length > 0) {
        setActiveEmissionTab('upcoming');
      } else if (emissionsData.withMiles.length > 0) {
        setActiveEmissionTab('withMiles');
      } else if (emissionsData.withoutMiles.length > 0) {
        setActiveEmissionTab('withoutMiles');
      }
    }
  }, [emissionsData, upcomingFlights]);

  // Mutation para sincronizar dados
  const syncMutation = useMutation({
    mutationFn: async () => {
      const response = await clientAPI.sync();
      return response.data;
    },
    onSuccess: () => {
      const currentLastSync = data?.lastSync;

      setSyncMessage({
        type: 'loading',
        text: 'Aguarde, estamos atualizando suas informações!',
      });

      // Polling: verificar a cada 5 segundos se os dados foram atualizados
      let attempts = 0;
      const maxAttempts = 12; // 12 x 5s = 60 segundos máximo

      const checkForUpdates = async () => {
        attempts++;

        // Revalidar dados
        await queryClient.invalidateQueries({ queryKey: ['client-dashboard'] });

        // Esperar um pouco para os dados serem atualizados
        setTimeout(() => {
          const newData = queryClient.getQueryData(['client-dashboard']) as
            | { lastSync: string }
            | undefined;
          const newLastSync = newData?.lastSync;

          // Verificar se a data mudou
          if (newLastSync && newLastSync !== currentLastSync) {
            setSyncMessage({
              type: 'success',
              text: 'Dados atualizados com sucesso!',
            });

            setTimeout(() => {
              setSyncMessage(null);
            }, 3000);
          } else if (attempts < maxAttempts) {
            // Continuar verificando
            setTimeout(checkForUpdates, 5000);
          } else {
            // Timeout - forçar revalidação final
            setSyncMessage({
              type: 'success',
              text: 'Sincronização completa. Atualize a página se os dados não aparecerem.',
            });

            setTimeout(() => {
              setSyncMessage(null);
            }, 5000);
          }
        }, 500);
      };

      // Iniciar polling após 10 segundos (dar tempo para automação processar)
      setTimeout(checkForUpdates, 10000);
    },
    onError: (error: unknown) => {
      console.error('Erro na sincronização:', error);

      let message = 'Erro ao sincronizar. Tente novamente mais tarde.';

      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response
      ) {
        const response = error.response as { data: Record<string, unknown> };
        const responseData = response.data;

        console.error('Resposta do servidor:', response);

        if (responseData.message && typeof responseData.message === 'string') {
          message = responseData.message;
        } else if (
          responseData.error &&
          typeof responseData.error === 'object' &&
          'message' in responseData.error &&
          typeof responseData.error.message === 'string'
        ) {
          message = responseData.error.message;
        }
      } else if (error instanceof Error) {
        message = `Erro: ${error.message}`;
      }

      setSyncMessage({
        type: 'error',
        text: message,
      });

      // Limpar mensagem após 8 segundos
      setTimeout(() => {
        setSyncMessage(null);
      }, 8000);
    },
  });

  // Filtrar programas com pontos > 0 e ordenar por ownership
  const programs: ClientProgram[] = (data?.programs || [])
    .filter((p: ClientProgram) => p.points > 0)
    .sort((a: ClientProgram, b: ClientProgram) => {
      // Primeiro ordena por ownership priority
      const priorityDiff = getOwnershipPriority(a.ownership) - getOwnershipPriority(b.ownership);
      if (priorityDiff !== 0) return priorityDiff;

      // Se mesmo ownership, ordena alfabeticamente por nome do programa
      return a.programName.localeCompare(b.programName, 'pt-BR');
    });
  const lastSync = data?.lastSync
    ? new Date(data.lastSync).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  // Datas de Adesão e Renovação
  const adesaoDate = data?.adesaoDate;
  const renovacaoDate = data?.renovacaoDate;

  // Últ. Renovação - Só exibe se tiver valor
  const ultimaRenovacaoDate = data?.ultimaRenovacaoDate;

  // Economias - Só exibir se for acima de 5000
  const economiaTotal =
    data?.economiaTotal && data.economiaTotal > 5000 ? data.economiaTotal : null;
  // Economia Vigente - Só exibir se tiver Últ. Renovação E for acima de 5000
  const economiaVigente =
    ultimaRenovacaoDate && data?.economiaVigente && data.economiaVigente > 5000
      ? data.economiaVigente
      : null;

  // Usar a informação do backend sobre se pode sincronizar
  const canSync = data?.canSyncNow ?? true;

  // Verificar se a renovação está vencida
  const renewalExpired = renovacaoDate && isRenewalExpired(renovacaoDate);

  // Calcular tempo restante até próxima sincronização
  const getTimeUntilNextSync = () => {
    if (!data?.lastAutomationSync) return null;
    const lastSyncDate = new Date(data.lastAutomationSync);
    const nextSync = new Date(lastSyncDate.getTime() + 60 * 60 * 1000); // +1 hora
    const now = new Date();
    const diff = nextSync.getTime() - now.getTime();

    if (diff <= 0) return null;

    const minutes = Math.ceil(diff / (1000 * 60));
    if (minutes < 60) return `${minutes} min`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
  };

  // Handler para sincronizar
  const handleSync = () => {
    if (canSync && !syncMutation.isPending) {
      syncMutation.mutate();
    }
  };

  // Loading state - aguardar todos os dados
  if (isLoading || isLoadingEmissions || isLoadingNextTrip) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Erro ao carregar dados</h2>
          <p className="text-muted-foreground mb-4">
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (programs.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Olá, {user?.name?.split(' ')[0] || 'Cliente'}!
          </h1>
          <p className="text-muted-foreground mt-1">Acompanhe sua gestão de viagens</p>
        </div>

        <div className="flex items-center justify-center h-96">
          <div className="text-center max-w-md">
            <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Nenhum programa encontrado
            </h2>
            <p className="text-muted-foreground">
              Seus programas de pontos aparecerão aqui assim que forem sincronizados.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerta de renovação vencida */}
      {renewalExpired && <RenewalExpiredAlert renewalDate={renovacaoDate!} />}

      {/* Conteúdo protegido - só renderiza se renovação NÃO estiver vencida */}
      {!renewalExpired && (
        <>
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Olá, {user?.name?.split(' ')[0] || 'Cliente'}!
          </h1>
          <p className="text-muted-foreground mt-1">Acompanhe sua gestão de viagens</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {lastSync && (
            <div className="flex items-center text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 mr-2" />
              <span>Última atualização: {lastSync}</span>
            </div>
          )}
          <button
            onClick={handleSync}
            disabled={!canSync || syncMutation.isPending}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold transition-all ${
              !canSync || syncMutation.isPending
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg active:scale-95'
            }`}
            title={
              !canSync && getTimeUntilNextSync()
                ? `Aguarde ${getTimeUntilNextSync()} para sincronizar novamente`
                : syncMutation.isPending
                  ? 'Sincronização em andamento...'
                  : 'Sincronizar dados do Notion'
            }
          >
            <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            <span>
              {syncMutation.isPending
                ? 'Sincronizando...'
                : !canSync && getTimeUntilNextSync()
                  ? `Aguarde ${getTimeUntilNextSync()}`
                  : 'Sincronizar Dados'}
            </span>
          </button>
        </div>
      </div>

      {/* Cards de Informações do Cliente */}
      {(adesaoDate || renovacaoDate || ultimaRenovacaoDate || economiaTotal || economiaVigente) && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {/* Card de Data de Adesão */}
          {adesaoDate && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-600 dark:bg-blue-500 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">
                    Data de Adesão
                  </p>
                  <p className="text-xl font-bold text-blue-900 dark:text-blue-100">
                    {adesaoDate ? formatDate(adesaoDate, 'dd/MM/yyyy') : 'Não disponível'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Card de Última Renovação */}
          {ultimaRenovacaoDate && (
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-600 dark:bg-purple-500 rounded-lg">
                  <CalendarClock className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-1">
                    Início do Plano Vigente
                  </p>
                  <p className="text-xl font-bold text-purple-900 dark:text-purple-100">
                    {ultimaRenovacaoDate
                      ? formatDate(ultimaRenovacaoDate, 'dd/MM/yyyy')
                      : 'Não disponível'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Card de Próxima Renovação */}
          {renovacaoDate && (
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-600 dark:bg-emerald-500 rounded-lg">
                  <CalendarClock className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-1">
                    Próxima Renovação
                  </p>
                  <p className="text-xl font-bold text-emerald-900 dark:text-emerald-100">
                    {renovacaoDate ? formatDate(renovacaoDate, 'dd/MM/yyyy') : 'Não disponível'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Card de Economia Total */}
          {economiaTotal && (
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-600 dark:bg-amber-500 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">
                    Economia Total
                  </p>
                  <p className="text-xl font-bold text-amber-900 dark:text-amber-100">
                    {formatCurrency(economiaTotal)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Card de Economia Vigente */}
          {economiaVigente && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-600 dark:bg-green-500 rounded-lg">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">
                    Economia no Plano Vigente
                  </p>
                  <p className="text-xl font-bold text-green-900 dark:text-green-100">
                    {formatCurrency(economiaVigente)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mensagem de sincronização */}
      {syncMessage && (
        <div
          className={`p-4 rounded-lg border flex items-center gap-3 ${
            syncMessage.type === 'loading'
              ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-500 text-blue-800 dark:text-blue-300'
              : syncMessage.type === 'success'
                ? 'bg-green-50 dark:bg-green-950/20 border-green-500 text-green-800 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-950/20 border-red-500 text-red-800 dark:text-red-300'
          }`}
        >
          {syncMessage.type === 'loading' && (
            <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />
          )}
          <span className="font-medium">{syncMessage.text}</span>
        </div>
      )}

      {/* Próxima Viagem - Compacto e Responsivo */}
      {!isLoadingNextTrip && nextTripData && (
        <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-card to-secondary/30">
          <CardContent className="p-4 sm:p-6">
            {/* Header com contador */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Plane className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <h3 className="text-sm sm:text-base font-bold text-foreground">Próxima Viagem</h3>
              </div>
              <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-full">
                <Clock className="h-3 sm:h-3.5 w-3 sm:w-3.5 text-green-700 dark:text-green-400" />
                <span className="text-xs sm:text-sm font-semibold text-green-700 dark:text-green-400">{getTimeUntilTrip(nextTripData.dataHoraIda)}</span>
              </div>
            </div>

            {/* Layout Principal: Rota + Detalhes */}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4 lg:gap-6">
              {/* Rota de Voo */}
              <div className="flex-1 flex items-center gap-2 sm:gap-4">
                {/* Origem */}
                <div className="text-center flex-shrink-0">
                  <div className="text-2xl sm:text-3xl font-bold text-primary tracking-tight">
                    {nextTripData.iataOrigem || '-'}
                  </div>
                  {nextTripData.dataHoraIda && (
                    <div className="text-xs text-muted-foreground mt-1">
                      <div className="font-medium">Ida</div>
                      <div>{formatDate(nextTripData.dataHoraIda, 'dd/MM HH:mm')}</div>
                    </div>
                  )}
                </div>

                {/* Linha de Voo */}
                <div className="flex-1 flex items-center gap-1 sm:gap-2 px-1 sm:px-3 min-w-0">
                  <div className="h-0.5 flex-1 bg-gradient-to-r from-primary to-primary/40" />
                  <Plane className="h-3 w-3 sm:h-4 sm:w-4 text-primary rotate-90 flex-shrink-0" />
                  <div className="h-0.5 flex-1 bg-gradient-to-r from-primary/40 to-primary" />
                </div>

                {/* Destino */}
                <div className="text-center flex-shrink-0">
                  <div className="text-2xl sm:text-3xl font-bold text-primary tracking-tight">
                    {nextTripData.iataDestino || '-'}
                  </div>
                  {nextTripData.dataHoraVolta && (
                    <div className="text-xs text-muted-foreground mt-1">
                      <div className="font-medium">Volta</div>
                      <div>{formatDate(nextTripData.dataHoraVolta, 'dd/MM HH:mm')}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Separador */}
              <div className="hidden lg:block h-16 w-px bg-border flex-shrink-0" />
              <div className="lg:hidden h-px w-full bg-border" />

              {/* Detalhes do Voo */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                {/* Localizador em Destaque */}
                {nextTripData.locCompanhia && (
                  <div className="text-center px-3 sm:px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg flex-shrink-0">
                    <div className="text-xs font-medium text-primary/70 uppercase tracking-wide mb-1">
                      Localizador
                    </div>
                    <div className="text-lg sm:text-xl font-bold font-mono text-primary tracking-wider">
                      {nextTripData.locCompanhia}
                    </div>
                  </div>
                )}

                {/* Informações Adicionais */}
                <div className="space-y-2 flex-1 min-w-0">
                  {/* Companhia com Destaque */}
                  {nextTripData.companhia && (
                    <div className="flex items-center gap-2">
                      <Plane className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm sm:text-base font-bold text-foreground truncate">
                        {nextTripData.companhia}
                      </span>
                    </div>
                  )}

                  {/* Tags de Tipo, Classe e Conexão */}
                  <div className="flex flex-wrap gap-1.5">
                    {nextTripData.classe && (
                      <span className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-1 text-xs font-semibold bg-primary/10 text-primary border border-primary/20 rounded-md">
                        <Award className="h-3 w-3 flex-shrink-0" />
                        <span className="whitespace-nowrap">{nextTripData.classe}</span>
                      </span>
                    )}
                    {nextTripData.tipo && (
                      <span className="inline-block px-2 sm:px-2.5 py-1 text-xs font-medium bg-secondary text-secondary-foreground border border-border rounded-md whitespace-nowrap">
                        {nextTripData.tipo}
                      </span>
                    )}
                    {nextTripData.temConexao && (
                      <span className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 rounded-md">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />
                        <span className="whitespace-nowrap">Conexão</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seção de Lojas Favoritas */}
      <FavoritePartnersSection />

      {/* Seção de Pontuações */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground">Suas pontuações</h2>

        {/* Programs Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {programs.map((program) => {
          const logo = getProgramLogo(program.programName);
          const ownershipColor = getOwnershipColor(program.ownership);
          const updateInfo = formatUpdateDate(program.manualUpdateDate);

          const hasExpiration =
            program.pointsExpiring !== null && program.pointsExpiring > 0 && program.expirationDate;

          return (
            <div
              key={program.id}
              className={`rounded-xl p-5 transition-all hover:shadow-lg flex flex-col ${
                hasExpiration
                  ? 'border-2 border-red-500 dark:border-red-600 shadow-red-200/50 dark:shadow-red-900/30 shadow-md bg-red-50/50 dark:bg-red-950/20'
                  : 'bg-card border border-border hover:border-primary/50'
              }`}
            >
              {/* Logo e Nome */}
              <div className="flex items-start gap-3 mb-5">
                {/* Logo */}
                <div className="flex-shrink-0">
                  {logo ? (
                    <img
                      src={logo}
                      alt={program.programName}
                      className="h-12 w-12 object-contain"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Plane className="h-6 w-6 text-primary" />
                    </div>
                  )}
                </div>

                {/* Nome e Badges */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-foreground truncate leading-tight">
                    {program.programName}
                  </h3>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {program.tier && (
                      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-md bg-primary/10 text-primary">
                        {program.tier}
                      </span>
                    )}
                    {program.ownership && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ${ownershipColor}`}
                      >
                        <User className="h-3 w-3 mr-1" />
                        {program.ownership}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Pontos Disponíveis + Expiração Inline */}
              <div className="mb-auto">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                  Saldo
                </p>
                <p className="text-3xl font-bold text-foreground mb-2">
                  {program.points.toLocaleString('pt-BR')}
                </p>

                {/* Alerta de Expiração - Inline abaixo do saldo */}
                {hasExpiration && (
                  <div className="flex items-center gap-1.5 text-red-700 dark:text-red-400 mt-2">
                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                    <div className="text-xs leading-tight">
                      <span className="font-bold">
                        {program.pointsExpiring!.toLocaleString('pt-BR')}
                      </span>
                      <span className="text-muted-foreground mx-1">•</span>
                      <span>
                        Expira{' '}
                        {parseDate(program.expirationDate!).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Última Atualização */}
              <div className="pt-3 mt-3 border-t border-border">
                <p className="text-xs flex items-center gap-1.5">
                  <RefreshCw
                    className={`h-3 w-3 ${
                      updateInfo.isOld
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-muted-foreground'
                    }`}
                  />
                  <span
                    className={
                      updateInfo.isOld
                        ? 'text-orange-600 dark:text-orange-400 font-medium'
                        : 'text-muted-foreground'
                    }
                  >
                    Última atualização: {updateInfo.text}
                  </span>
                </p>
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Emissões - Card Único com Tabs */}
      {!isLoadingEmissions && emissionsData && (emissionsData.withMiles.length > 0 || emissionsData.withoutMiles.length > 0 || upcomingFlights.length > 0) && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-foreground">Suas Emissões</h2>

          <Card>
            {/* Tabs Header - Só exibe abas que tenham emissões */}
            <div className="flex border-b border-border bg-muted/30">
              {/* Tab: Próximos Voos */}
              {upcomingFlights.length > 0 && (
                <button
                  onClick={() => setActiveEmissionTab('upcoming')}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-all relative ${
                    activeEmissionTab === 'upcoming'
                      ? 'text-primary bg-background'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <CalendarClock className="h-4 w-4" />
                    <span>Próximos Voos</span>
                    <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
                      {upcomingFlights.length}
                    </span>
                  </div>
                  {activeEmissionTab === 'upcoming' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  )}
                </button>
              )}
              {emissionsData.withMiles.length > 0 && (
                <button
                  onClick={() => setActiveEmissionTab('withMiles')}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-all relative ${
                    activeEmissionTab === 'withMiles'
                      ? 'text-green-600 dark:text-green-400 bg-background'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Award className="h-4 w-4" />
                    <span>Com Milhas</span>
                    <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                      {emissionsData.withMiles.length}
                    </span>
                  </div>
                  {activeEmissionTab === 'withMiles' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600 dark:bg-green-400" />
                  )}
                </button>
              )}
              {emissionsData.withoutMiles.length > 0 && (
                <button
                  onClick={() => setActiveEmissionTab('withoutMiles')}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-all relative ${
                    activeEmissionTab === 'withoutMiles'
                      ? 'text-blue-600 dark:text-blue-400 bg-background'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Plane className="h-4 w-4" />
                    <span>Sem Milhas</span>
                    <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      {emissionsData.withoutMiles.length}
                    </span>
                  </div>
                  {activeEmissionTab === 'withoutMiles' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                  )}
                </button>
              )}
            </div>

            {/* Tabs Content */}
            <CardContent className="p-0">
              {/* Tab: Próximos Voos */}
              {activeEmissionTab === 'upcoming' && upcomingFlights.length > 0 && (
                <Table>
                  <TableHeader className="bg-muted">
                    <TableRow>
                      <TableHead>Trecho</TableHead>
                      <TableHead>Companhia</TableHead>
                      <TableHead>Loc. Cia</TableHead>
                      <TableHead>Sobrenome</TableHead>
                      <TableHead>Data Ida</TableHead>
                      <TableHead>Data Volta</TableHead>
                      <TableHead>Com Milhas</TableHead>
                      <TableHead>Valor Pago</TableHead>
                      <TableHead>Valor Mercado</TableHead>
                      <TableHead>V. Economizado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upcomingFlights.map((emission) => (
                      <TableRow key={emission.id}>
                        <TableCell className="font-medium">{emission.trecho}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.companhia || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.locCompanhia || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.sobrenome || '-'}
                        </TableCell>
                        <TableCell className={`text-sm ${getDateColorClasses(emission.dataHoraIda)}`}>
                          {emission.dataHoraIda
                            ? formatDate(emission.dataHoraIda, 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell className={`text-sm ${getDateColorClasses(emission.dataHoraVolta)}`}>
                          {emission.dataHoraVolta
                            ? formatDate(emission.dataHoraVolta, 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {emission.withMiles ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md">
                              <Award className="h-3 w-3" />
                              Sim
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md">
                              <Plane className="h-3 w-3" />
                              Não
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.withMiles
                            ? emission.taxa !== null
                              ? formatCurrency(emission.taxa)
                              : '-'
                            : emission.vVenda !== null
                              ? formatCurrency(emission.vVenda)
                              : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.vCompanhia !== null
                            ? formatCurrency(emission.vCompanhia)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {emission.vEconomizado !== null
                            ? formatCurrency(emission.vEconomizado)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Tab: Com Milhas */}
              {activeEmissionTab === 'withMiles' && emissionsData.withMiles.length > 0 && (
                <Table>
                  <TableHeader className="bg-muted">
                    <TableRow>
                      <TableHead>Trecho</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Companhia</TableHead>
                      <TableHead>Classe</TableHead>
                      <TableHead>Loc. Cia</TableHead>
                      <TableHead>Data Ida</TableHead>
                      <TableHead>Data Volta</TableHead>
                      <TableHead>Valor Pago (Taxa)</TableHead>
                      <TableHead>Valor Mercado</TableHead>
                      <TableHead>V. Economizado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emissionsData.withMiles.map((emission) => (
                      <TableRow key={emission.id}>
                        <TableCell className="font-medium">{emission.trecho}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.tipo || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.companhia || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.classe || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.locCompanhia || '-'}
                        </TableCell>
                        <TableCell className={`text-sm ${getDateColorClasses(emission.dataHoraIda)}`}>
                          {emission.dataHoraIda
                            ? formatDate(emission.dataHoraIda, 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell className={`text-sm ${getDateColorClasses(emission.dataHoraVolta)}`}>
                          {emission.dataHoraVolta
                            ? formatDate(emission.dataHoraVolta, 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.taxa !== null ? formatCurrency(emission.taxa) : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.vCompanhia !== null
                            ? formatCurrency(emission.vCompanhia)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {emission.vEconomizado !== null
                            ? formatCurrency(emission.vEconomizado)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Tab: Sem Milhas */}
              {activeEmissionTab === 'withoutMiles' && emissionsData.withoutMiles.length > 0 && (
                <Table>
                  <TableHeader className="bg-muted">
                    <TableRow>
                      <TableHead>Trecho</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Companhia</TableHead>
                      <TableHead>Classe</TableHead>
                      <TableHead>Loc. Companhia</TableHead>
                      <TableHead>Data Ida</TableHead>
                      <TableHead>Data Volta</TableHead>
                      <TableHead>Valor Pago (V. Venda)</TableHead>
                      <TableHead>Valor Mercado</TableHead>
                      <TableHead>V. Economizado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emissionsData.withoutMiles.map((emission) => (
                      <TableRow key={emission.id}>
                        <TableCell className="font-medium">{emission.trecho}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.tipo || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.companhia || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.classe || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.locCompanhia || '-'}
                        </TableCell>
                        <TableCell className={`text-sm ${getDateColorClasses(emission.dataHoraIda)}`}>
                          {emission.dataHoraIda
                            ? formatDate(emission.dataHoraIda, 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell className={`text-sm ${getDateColorClasses(emission.dataHoraVolta)}`}>
                          {emission.dataHoraVolta
                            ? formatDate(emission.dataHoraVolta, 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.vVenda !== null ? formatCurrency(emission.vVenda) : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {emission.vCompanhia !== null
                            ? formatCurrency(emission.vCompanhia)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {emission.vEconomizado !== null
                            ? formatCurrency(emission.vEconomizado)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {/* Empty State */}
              {((activeEmissionTab === 'upcoming' && upcomingFlights.length === 0) ||
                (activeEmissionTab === 'withMiles' && emissionsData.withMiles.length === 0) ||
                (activeEmissionTab === 'withoutMiles' && emissionsData.withoutMiles.length === 0)) && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Plane className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {activeEmissionTab === 'upcoming'
                        ? 'Nenhum voo futuro encontrado'
                        : 'Nenhuma emissão encontrada nesta categoria'}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
        </>
      )}
    </div>
  );
}
